import { useEffect, useRef } from 'react';
import type { KeyboardEvent } from 'react';

interface CodeInputProps {
  length: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  label: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Renders filled boxes as dots — used for the PIN. */
  secret?: boolean;
}

/**
 * Fixed-length numeric code entry (OTP / PIN).
 * Digits are entered sequentially; the caret always sits on the first empty
 * box, which keeps mobile keyboards and RTL layout predictable.
 */
export function CodeInput({
  length,
  value,
  onChange,
  onComplete,
  label,
  disabled = false,
  invalid = false,
  secret = false,
}: CodeInputProps) {
  const boxes = useRef<Array<HTMLInputElement | null>>([]);
  const digits = value.slice(0, length);

  useEffect(() => {
    if (disabled) return;
    const index = Math.min(digits.length, length - 1);
    boxes.current[index]?.focus();
  }, [digits.length, length, disabled]);

  const commit = (next: string) => {
    const clean = next.replace(/\D+/g, '').slice(0, length);
    onChange(clean);
    if (clean.length === length) onComplete?.(clean);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      commit(digits.slice(0, -1));
    }
  };

  return (
    <div>
      <span className="block text-sm font-medium mb-2">{label}</span>
      <div className="flex justify-center gap-2" dir="ltr">
        {Array.from({ length }, (_, index) => {
          const digit = digits[index] ?? '';
          return (
            <input
              key={index}
              ref={(el) => {
                boxes.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label={`${label} ${index + 1}`}
              value={secret && digit ? '•' : digit}
              disabled={disabled}
              onChange={(event) => commit(digits + event.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={(event) => event.target.select()}
              className={[
                'w-11 h-13 sm:w-12 sm:h-14 text-center text-xl font-semibold rounded-xl border bg-white',
                'outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-100',
                invalid ? 'border-red-400' : 'border-line',
                disabled ? 'opacity-60' : '',
              ].join(' ')}
            />
          );
        })}
      </div>
    </div>
  );
}
